import { InternalAxiosRequestConfig } from 'axios'

import { checkRefreshToken } from '@app/api/auth/checkRefreshToken'
import config, { vars } from '@app/config'
import { getActiveB2CCookieNames, getAuthMode } from '@app/utils'

import { hasFeature, WebViewFeature } from '@app/utils/webview/hasFeature'

import { getUserIdFromToken } from './getUserIdFromToken'
import { getCookie, getTokenFromId, isTokenExpired } from './helpers'
import { getRefreshedToken } from './tokenRefreshManager'

export const UserAgentMiddleware = async (
  conf: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  try {
    const userAgentCookie = getCookie('userAgent')
    const userAgent = userAgentCookie || (navigator && navigator.userAgent)
    if (userAgent) {
      conf.headers = conf.headers || {}
      conf.headers['X-Original-User-Agent'] = userAgent
    }
  } catch (error) {
    console.error('Error in user agent middleware', error)
  }
  return conf
}

export const AuthorizationMiddleware = async (
  conf: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  const { isBackOfficeTokenMode, buyerTokenMode } = getAuthMode()
  const { accessTokenCookie, refreshTokenCookie } = getActiveB2CCookieNames()

  let accessToken = isBackOfficeTokenMode
    ? getCookie(vars.b2b_access_token)
    : getCookie(accessTokenCookie)

  const canDelegate =
    config.identity.isWebView && hasFeature(WebViewFeature.DelegatedJwtExchange)

  const doRefreshToken =
    !accessToken && !canDelegate
      ? isBackOfficeTokenMode
        ? getCookie(vars.b2b_refresh_token)
        : buyerTokenMode
          ? false
          : getCookie(refreshTokenCookie) ||
            (!buyerTokenMode && (await checkRefreshToken()))
      : false

  const shouldRefresh =
    (Boolean(accessToken) && isTokenExpired(accessToken) && !buyerTokenMode) ||
    (!accessToken && (Boolean(doRefreshToken) || canDelegate))

  if (shouldRefresh) {
    try {
      const refreshed = await getRefreshedToken(doRefreshToken)
      if (refreshed && refreshed.token) {
        accessToken = refreshed.token
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }

  if (accessToken) {
    const tokenUserId = getUserIdFromToken(accessToken)

    if (!tokenUserId) {
      const controller = new AbortController()
      conf.signal = controller.signal
      controller.abort('Missing user_id in access token, request cancelled.')
      await getTokenFromId()

      return Promise.reject(new Error('Missing user_id in access token'))
    }

    conf.headers.Authorization = `Bearer ${accessToken}`
  }

  return conf
}
